import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

// Floating AI Concierge chat, pre-loaded with a listing's context.
export const ConciergeChat = ({ slug, sessionId, agentName, onOpen }) => {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hi! I'm the Chateaux concierge for this listing. Ask me anything — square footage, the neighborhood, or booking a showing with ${agentName || "the agent"}.`,
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const openedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setPulse(true), 20000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const handleOpen = () => {
    setOpen(true);
    setPulse(false);
    if (!openedRef.current) {
      openedRef.current = true;
      onOpen && onOpen();
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/concierge/chat`, {
        slug,
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I hit a snag. Please try again in a moment." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={handleOpen}
            data-testid="concierge-fab"
            className={`fixed bottom-24 right-5 md:right-8 z-40 flex items-center gap-2 bg-ink text-bone pl-4 pr-5 py-3.5 rounded-full shadow-2xl hover:bg-coral transition-colors duration-300 ${
              pulse ? "animate-pulse" : ""
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Ask the concierge</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 md:right-8 z-40 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[70vh] bg-bone border border-ink/15 shadow-2xl flex flex-col rounded-2xl overflow-hidden"
            data-testid="concierge-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-ink text-bone">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-coral" />
                <span className="text-sm font-medium">Chateaux Concierge</span>
              </div>
              <button onClick={() => setOpen(false)} data-testid="concierge-close" aria-label="Close chat">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="concierge-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                    m.role === "user"
                      ? "ml-auto bg-coral text-bone rounded-br-sm"
                      : "mr-auto bg-ink/5 text-ink rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              ))}
              {loading && (
                <div className="mr-auto bg-ink/5 text-ink/50 px-3.5 py-2.5 text-sm rounded-2xl rounded-bl-sm">
                  Thinking…
                </div>
              )}
            </div>

            <div className="border-t border-ink/10 p-3 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about this home…"
                data-testid="concierge-input"
                className="flex-1 bg-transparent text-sm text-ink placeholder-ink/40 outline-none px-2"
              />
              <button
                onClick={send}
                disabled={loading}
                data-testid="concierge-send"
                className="bg-coral text-bone p-2.5 rounded-full hover:bg-ink transition-colors disabled:opacity-50"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
